// frontend/src/components/Avatar.tsx
import React, { useMemo } from 'react';

interface AvatarProps {
  sender: 'user' | 'ia';
  modelId?: string;
  size?: 'sm' | 'md' | 'lg';
  theme: 'light' | 'dark' | 'neon' | 'pastel';
}

const Avatar: React.FC<AvatarProps> = ({ sender, modelId, size = 'md', theme }) => {
  // Générer les classes de taille
  const sizeClass = `avatar--${size}`;
  
  // Obtenir un style différent en fonction du modèle d'IA
  const getModelStyle = useMemo(() => {
    if (sender !== 'ia' || !modelId) return {};

    // Génération d'une couleur cohérente basée sur le modelId pour les IA
    const generateModelColor = (modelId: string) => {
      // Hash simple pour générer une couleur cohérente
      let hash = 0;
      for (let i = 0; i < modelId.length; i++) {
        hash = modelId.charCodeAt(i) + ((hash << 5) - hash);
      }
      
      // Différentes palettes selon les thèmes
      const colorPalettes = {
        light: ['#3b82f6', '#60a5fa', '#93c5fd', '#2563eb', '#1d4ed8'],
        dark: ['#2563eb', '#1d4ed8', '#1e40af', '#3b82f6', '#60a5fa'],
        neon: ['#06b6d4', '#22d3ee', '#0ea5e9', '#0284c7', '#38bdf8'],
        pastel: ['#8b5cf6', '#a78bfa', '#c4b5fd', '#7c3aed', '#6d28d9']
      };
      
      const palette = colorPalettes[theme] || colorPalettes.light;
      return palette[Math.abs(hash) % palette.length];
    };
    
    const bgColor = generateModelColor(modelId);
    
    // Styles selon le modèle
    const modelVariants: { [key: string]: { backgroundColor: string; backgroundImage?: string; backgroundSize?: string } } = {
      'qwen2.5-coder': {
        backgroundColor: bgColor,
        backgroundImage: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0.3) 75%, transparent 75%, transparent)',
        backgroundSize: '8px 8px'
      },
      'mathstral': {
        backgroundColor: bgColor,
        backgroundImage: 'radial-gradient(circle at center, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 70%)'
      },
      'claude': {
        backgroundColor: bgColor,
        backgroundImage: 'linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%)',
        backgroundSize: '12px 12px'
      },
      'mixtral': {
        backgroundColor: bgColor,
        backgroundImage: 'repeating-linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1) 1px, transparent 1px, transparent 6px)'
      },
      'default': {
        backgroundColor: bgColor
      }
    };
    
    // Trouver le modèle correspondant
    const matchedModel = Object.keys(modelVariants).find(key => modelId.includes(key));
    return matchedModel ? modelVariants[matchedModel] : modelVariants.default;
  }, [modelId, sender, theme]); // Suppression de generateModelColor des dépendances
  
  // Style pour l'avatar de l'utilisateur
  const userAvatarStyle = useMemo(() => {
    const userColors = {
      light: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
      dark: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
      neon: 'linear-gradient(135deg, #06b6d4, #0284c7)',
      pastel: 'linear-gradient(135deg, #8b5cf6, #a78bfa)'
    };
    
    return {
      background: userColors[theme] || userColors.light
    };
  }, [theme]);

  // Générer une classe pour le type d'avatar
  const typeClass = `avatar--${sender}`;
  
  return (
    <div 
      className={`avatar ${sizeClass} ${typeClass}`}
      style={sender === 'user' ? userAvatarStyle : getModelStyle}
      aria-label={sender === 'user' ? 'Avatar de l\'utilisateur' : 'Avatar de l\'IA'}
    >
      {sender === 'user' ? (
        <span className="avatar__initial">U</span>
      ) : (
        <span className="avatar__initial">AI</span>
      )}
    </div>
  );
};

export default Avatar;